import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { Calendar, MapPin, Clock, User, Repeat } from 'lucide-react';

// Helper function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export function EventInfo({ event }) {
  const eventDate = event.date ? new Date(event.date) : null;
  const eventEndDate = event.end_date ? new Date(event.end_date) : null;
  const isRecurringEvent = event.is_recurring;
  const isExhibition = event.category === 'exhibition';

  return (
    <div className="lg:col-span-2 space-y-8">
      {/* Date & Time Info */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date/Time Section */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                {isRecurringEvent ? (
                  <Repeat className="w-6 h-6 text-primary" />
                ) : (
                  <Calendar className="w-6 h-6 text-primary" />
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {isRecurringEvent ? 'Schedule' : 'Date & Time'}
                </p>
                {isRecurringEvent ? (
                  <div>
                    <p className="font-semibold text-foreground capitalize">
                      {event.recurring_schedule?.days?.join(', ') || 'Multiple Days'}
                    </p>
                    <p className="text-muted-foreground">
                      {formatTime12Hour(event.recurring_schedule?.opening_time)} - {formatTime12Hour(event.recurring_schedule?.closing_time)}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="font-semibold text-foreground">
                      {eventDate ? format(eventDate, 'EEEE, MMMM d, yyyy') : 'Date TBA'}
                    </p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {eventDate ? format(eventDate, 'h:mm a') : 'Time TBA'}
                      {eventEndDate && (
                        <span className="text-muted-foreground">
                          - {format(eventEndDate, isExhibition ? 'MMM d, yyyy' : 'h:mm a')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Location Section */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-secondary/10">
                <MapPin className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Location</p>
                <p className="font-semibold text-foreground">{event.venue}</p>
                <p className="text-muted-foreground">{event.address}, {event.city}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About Section */}
      <Card>
        <CardHeader>
          <CardTitle>About This Event</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {event.description}
          </p>
        </CardContent>
      </Card>

      {/* Organizer */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-muted">
              <User className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Organized by</p>
              <p className="font-semibold text-foreground">{event.organizer_name}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
